/**
 * Survival Module - PSP/CRE Tool v3.0
 * 
 * Handles time-dependent survival calculations including:
 * - TCCC interventions (tourniquet, TXA, hypothermia)
 * - Platinum 10 Minutes modeling
 * - Golden Hour calculations
 * - DOW (Died of Wounds) rate determination
 * 
 * @version 5.6.0
 * @date March 27, 2026
 *
 * VERSION 5.6.0 — ENH-039: R2B bed occupancy drain wired into calculateComprehensiveDOW() (March 27, 2026)
 * - IMPORT: BED_OCCUPANCY_MODEL from mtfCapabilities.js. Thresholds: optimal=70%, warning=85%, critical=95%.
 * - NEW PARAM: bedOccupancyPct in calculateComprehensiveDOW() config (default 0 = no effect).
 *   Passed by casualtyCalculator.js day loop from running bedCensus / r2bBedCapacity × 100.
 * - Step 11.6 in DOW chain: map occupancy to capability degradation using BED_OCCUPANCY_MODEL thresholds,
 *   apply getDOWMultiplierFromCapability() as additional multiplier on adjustedDOWRate.
 *   GREEN (<70%): 1.0× | YELLOW (70-85%): 1.1× | ORANGE (85-95%): 1.3× | RED (≥95%): 1.5×.
 *   When bedOccupancyPct=0 (no data): no effect. Backward compatible.
 *
 * VERSION 5.5.0 — ENH-032: Night ops treatment quality wired into calculateComprehensiveDOW() (March 27, 2026)
 * - WIRED: NIGHT_OPS_LEVELS.treatment_quality from nightOperations.js via scenario
 *   (scenario.operation_parameters.night_ops_config.modifiers.treatment_modifier, pre-computed by Tab2).
 * - Applied in calculateComprehensiveDOW() as step 11.5: adjustedDOWRate *= (1.0 / treatment_modifier).
 *   Formula: lower treatment quality → inverse multiplier → higher DOW rate.
 *   NONE=1.0 (×1.0), LIMITED=0.95 (×1.053), MODERATE=0.90 (×1.111), HIGH/CONTINUOUS=0.85 (×1.176).
 * - When nightOpsConfig.enabled=false or absent, treatment_modifier=1.0 (no change).
 *
 * VERSION 5.4.0 — WTD-01: Wound-type DOW refinement (March 3, 2026)
 * - Added optional woundDistribution + operationType params to calculateComprehensiveDOW().
 * - When provided by casualtyCalculator, baseDOWRate refined using wound-type-weighted
 *   DOW rates (JTS JTTR) via getWeightedDOWRate() from woundTypeDistribution.js.
 * - STANDARD_BASELINE T1 weighted DOW ~12.8%; MECHANIZED ~13.7%; URBAN_ASSAULT ~12.2%.
 * - All downstream modifiers (MTF, blood, triage, paradigm) continue unchanged.
 * - Zero regression: when woundDistribution is null (default), identical to v5.3.1.
 * - New return fields: modifiers_applied.wound_type_applied (bool), .operation_type (string).
 *
 * VERSION 5.3.1 — F-02: ARCH-012 pattern fixed in distance reads (March 1, 2026)
 * Two key mismatch sites confirmed and fixed. Same root cause as ARCH-012 in medevacModule.
 * Tab3 buildUpdates() saves mtf_distances.role2_distance_km. Both read sites used the
 * non-existent short form mtf_distances.role2 — optional chaining silently returned undefined,
 * fallback always fired (APP_RULES 6.1 violation).
 *
 * F-02 SITE 1 FIX (Line ~765) — timeToRole2 in calculateTCCCInterventionEffects call:
 *   Was: medicalConfig?.mtf_distances?.role2 (short form, never exists in Tab3 schema)
 *   Effect: condition always falsy → timeToRole2 always 90 min regardless of Tab3 config
 *   Consequence: pfcRequired = 90 > 60 = true for every casualty always; pfcDuration
 *   always 30 min — PFC permanently activated even at 15 km distances.
 *   Fix: read role2_distance_km. Null-check uses != null (not ||) to allow distance=0.
 *
 * F-02 SITE 2 FIX (Line ~782) — role2DistanceKm local variable:
 *   Was: medicalConfig?.mtf_distances?.role2 || 25 (short form, never exists)
 *   Effect: role2DistanceKm always 25 km
 *   Primary impact: fallback evacuation path (no medevacAnalysis provided) and Monte Carlo
 *   at line ~1037, which runs outside the medevacAnalysis guard.
 *   Fix: read role2_distance_km. Uses ?? operator (not ||) to avoid swallowing 0.
 *
 * VERSION 5.3 - SVM-09 fallback constants migration (February 24, 2026)
 * - SVM-09: All BUG-057 NaN-guard fallback literals migrated to named constants.
 *   T1: 0.95→T1_SURVIVAL_FALLBACK_AT_30MIN, 0.005→T1_SURVIVAL_DECAY_RATE_PER_MIN,
 *   0.50→T1_SURVIVAL_FALLBACK_FLOOR (from CCC v2.2).
 *   T2: 0.95→T2_SURVIVAL_FALLBACK (from CCC v2.2).
 *   T3: 0.98→T3_SURVIVAL_CURVE.survival_windows.under_72_hours.rate (canonical, exact match).
 *   DOW×2: 0.12→NATO_BASE_DOW_RATE (from CCC v2.2, both NaN-guard sites).
 * VERSION 5.2 - SVM-02/06/07 constants migration (February 24, 2026)
 * - SVM-02: Local CLS_TRAINING_QUALITY deleted. CLS_TRAINING_QUALITY_CANONICAL imported
 *   from operationsConfig.js v2.2. CLS_KEY_MAP adapter (EXCELLENT→ADVANCED, GOOD→STANDARD,
 *   ADEQUATE→STANDARD, MINIMAL→BASIC, NONE→UNTRAINED). resolveClsConfig() synthesizes
 *   .tourniquet_time_modifier from .tourniquet_correct_rate (2.0 - rate*1.5 formula).
 *   CLS_TRAINING_QUALITY flat map rebuilt for re-export. Usage site updated to use
 *   resolveClsConfig().
 * - SVM-06: Inline threatDelays object deleted. THREAT_LEVELS imported from
 *   operationsConfig.js. THREAT_DELAY_KEY_MAP adapter (LOW→LOW_THREAT, INTENSE/PEER→
 *   EXTREME_THREAT). Reads .medical.first_aid_delay_minutes (EXT-06 values: 2/5/10/20).
 * - SVM-07: `baselineNoIntervention = 0.60` literal replaced with
 *   T1_BASELINE_NO_INTERVENTION from casualtyCalculatorConstants.js v2.2.
 * VERSION 5.1 - SVM-03 constants migration (February 24, 2026)
 * - SVM-03: Local TERRAIN_FIRST_AID_MODIFIERS deleted. TERRAIN_TYPES_TF imported from
 *   terrainFactors.js v1.1.0. TERRAIN_FA_KEY_MAP adapter normalises legacy keys.
 *   TERRAIN_FIRST_AID_MODIFIERS rebuilt as computed Object.fromEntries from canonical
 *   .time_modifier values. Re-export at bottom preserved for consuming modules.
 * - SVM-03: Updated deferred note in header.
 * @version 5.0
 * @author Dr. Tatar Radu MD
 * @date February 23, 2026
 *
 * VERSION 5.0 - Phase 4-C partial constants migration (February 23, 2026)
 * - SVM-01: Extended prehospitalInterventions.js import block — added
 *   NIGHT_FIRST_AID_MODIFIER, BASE_CLS_RESPONSE_MINUTES, DOW_REDUCTION_RATES.
 * - SVM-04: Deleted local `const NIGHT_FIRST_AID_MODIFIER = 1.25` — now imported
 *   from prehospitalInterventions.js (EXT-07 delivered this). Re-exported for
 *   backward compatibility.
 * - SVM-05: `baseTimeMinutes = 3` literal replaced with BASE_CLS_RESPONSE_MINUTES
 *   from prehospitalInterventions.js.
 * - SVM-08: `(tourniquetApplied ? 25 : 0)` replaced with
 *   `Math.round(DOW_REDUCTION_RATES.TOURNIQUET_ONLY * 100)` — canonical = 25.
 *
 * DEFERRED items:
 * - SVM-02: CLS_TRAINING_QUALITY local block — canonical in operationsConfig.js
 *   (EXT-06) has UNTRAINED/BASIC/STANDARD/ADVANCED keys and survival_modifier field;
 *   local has EXCELLENT/GOOD/ADEQUATE/MINIMAL/NONE keys and tourniquet_time_modifier.
 *   Structural mismatch requires function refactor. Deferred to Phase 5+.
 * - SVM-03: ✅ DONE in v5.1 — see VERSION 5.1 above.
 * - SVM-06: threatDelays local object — key mismatch: local LOW/MEDIUM/HIGH/INTENSE/PEER
 *   vs canonical LOW_THREAT/MODERATE_THREAT/HIGH_THREAT/EXTREME_THREAT. INTENSE and PEER
 *   have no canonical equivalent. Deferred to Phase 5+.
 * - SVM-07: baselineNoIntervention = 0.60 — survivalCurves.js does not exist yet.
 * - SVM-09: ✅ DONE in v5.3 — all fallback literals replaced with named constants (see VERSION 5.3).
 * - REMOVED: All diagnostic console.log statements (🔍 [TCCC DEBUG], 🔍 [PARADIGM DEBUG])
 * - VERIFIED: v4.5 fixes working correctly (0 warnings, clean console)
 * - PRODUCTION READY: Clean console output, all bugs fixed
 * - RETAINED: CRITICAL error logs for zero tolerance architecture
 * - LOGS REMOVED:
 *   - Lines ~354-390: TCCC diagnostic logs (3 console.log statements)
 *   - Lines ~620-636: Paradigm diagnostic logs (5 console.log statements)
 * - RESULT: Production-clean code, zero console spam
 * 
 * VERSION 4.5 FIXES (February 15, 2026) - TCCC NaN COMPLETE FIX:
 * - TCCC ROOT CAUSE 1: Property name mismatch
 *   - calculateCombinedInterventionEffect returns: survival_rate (not survival_modifier)
 *   - Code expected: survival_modifier (undefined)
 *   - FIX: Try survival_modifier first, fallback to survival_rate
 * - TCCC ROOT CAUSE 2: Object vs Number type mismatch
 *   - calculateTXAEffectiveness returns: {rate: 0.32, icon: "🟢", ...} (object)
 *   - calculateHypothermiaMortalityMultiplier returns: {multiplier: 2, icon: "🔴", ...} (object)
 *   - Code expected: numeric values for math operations (1.0, 2.0)
 *   - Math on objects: 1 / {rate: 0.32} = NaN
 *   - FIX: Extract numeric values (.rate, .multiplier) from objects before calculation
 * - RESULT: Eliminates all TCCC NaN warnings, uses actual intervention effects
 * - LOCATIONS: Lines ~320-360 (TCCC calculation section)
 * - DIAGNOSTIC KEPT: Logs remain for verification
 * 
 * VERSION 4.4.1 DIAGNOSTIC (February 15, 2026) - TCCC NaN INVESTIGATION:
 * - ADDED: Diagnostic logging in calculateTCCCInterventionEffects
 * - PURPOSE: Identify why tcccAnalysis.combined.survival_modifier returns NaN
 * - LOGS: Input values before calculation, calculateCombinedInterventionEffect result, final modifier
 * - LOCATION: Lines ~324-360 (TCCC combined calculation section)
 * - TEMPORARY: These logs will identify which imported function returns NaN/undefined
 * - NOTE: TCCC warnings non-blocking, calculations use fallback 1.0 safely
 * 
 * VERSION 4.4 FIXES (February 15, 2026) - ZERO TOLERANCE ARCHITECTURE:
 * - PHILOSOPHY: "Fallbacks mask validation errors" (APP_RULES 6.1)
 * - REMOVED: All defensive fallbacks to default values (0.85)
 * - CHANGED: Now uses paradigmConfig.t1_survival_rate DIRECTLY
 * - VALIDATION: If t1_survival_rate missing, logs CRITICAL error
 * - FAILURE MODE: Uses obviously wrong value (0.01 = 1% survival) to expose bug
 * - RATIONALE: Forces fix of evacuationParadigms.js rather than masking issues
 * - IMPACT: If constants file broken, results will be obviously wrong (not silently wrong)
 * - LOCATIONS: Line ~806-815 (calculation), Line ~958 (reporting)
 * - COMPLIANCE: APP_RULES 6.1 "No Fallback Mechanisms"
 * 
 * VERSION 4.3 FIXES (February 15, 2026) - BUG-057 ACTUAL FIX:
 * - BUG-057 ROOT CAUSE: Property name mismatch between constants and code
 *   - evacuationParadigms.js defines: t1_survival_rate (e.g., 0.76 = 76% survival)
 *   - survivalModule.js expected: t1_survival_modifier (was undefined)
 *   - DIAGNOSTIC: Added logging revealed paradigmConfig has t1_survival_rate, not t1_survival_modifier
 *   - FIX: Use t1_survival_rate as fallback when t1_survival_modifier missing
 *   - LOGIC: paradigmModifier = t1_survival_modifier || t1_survival_rate || 0.85
 *   - LOCATIONS: Line 803-806 (calculation), Line 958 (reporting)
 *   - RESULT: Eliminates all ~280 "[BUG-057 FIX] paradigmConfig.t1_survival_modifier was invalid" warnings
 *   - NOTE: Both properties represent survival rates, so direct substitution is mathematically valid
 * 
 * VERSION 4.2.1 DIAGNOSTIC (February 15, 2026) - BUG-057 INVESTIGATION:
 * - ADDED: Comprehensive diagnostic logging for paradigm lookup
 * - PURPOSE: Understand why v4.2 fix didn't eliminate warnings
 * - LOGS: paradigm value, lookup result, final config, t1_survival_modifier presence
 * - LOCATION: Line 531-548 (paradigmConfig assignment)
 * - DIAGNOSTIC KEPT: Logs remain for validation
 * 
 * VERSION 4.2 FIXES (February 15, 2026) - BUG-057 INCOMPLETE FIX:
 * - BUG-057 FINAL FIX: paradigmConfig fallback logic corrected
 *   - ROOT CAUSE: Ternary operator checked if getParadigm EXISTS, not if it returns valid data
 *   - OLD LOGIC: getParadigm ? getParadigm(paradigm) : (EVACUATION_PARADIGMS[paradigm] || fallback)
 *   - PROBLEM: If getParadigm() returns undefined, fallback was never used
 *   - NEW LOGIC: (getParadigm ? getParadigm(paradigm) : EVACUATION_PARADIGMS[paradigm]) || fallback
 *   - RESULT: Fallback now properly used when lookup returns undefined/null
 *   - IMPACT: Eliminates ~280 console warnings about invalid t1_survival_modifier
 *   - LOCATION: Line 531-536 (paradigmConfig assignment)
 *   - RELATED: Tab2 v7.4.2 correctly saves evacuation_paradigm, now properly consumed
 * 
 * VERSION 4.1 FIXES (January 23, 2026):
 * - BUG-057 FIX: NaN cascade in DOW calculation causing WIA=0
 *   - ROOT CAUSE: calculateT1Survival/T2Survival/T3Survival returning undefined
 *   - PROPAGATION: undefined → NaN → baseDOWRate → adjustedDOWRate → dow_rate
 *   - SYMPTOM: WIA = originalWIA - NaN = NaN → aggregated as 0
 *   - SOLUTION: Added defensive checks with NATO-accurate fallback values:
 *     - T1 survival: 95% at 30min, -0.5%/min beyond, floor 50%
 *     - T2 survival: 95% (stable with timely surgery)
 *     - T3 survival: 98% (minor injuries)
 *     - Base DOW rate fallback: 12% (NATO AJMedP-1 standard)
 *   - Added guards for all modifier multipliers (MTF, blood, triage, paradigm)
 *   - Final sanity clamp: DOW rate between 2% and 45%
 */

// ============================================================================
// IMPORTS
// ============================================================================

// CALC-010: documented default + "assumptions used" ledger instead of a silent literal fallback
import { resolveInput } from '../assumptionLedger.js';

import {
  SURVIVAL_TIME_WINDOWS,
  T1_SURVIVAL_CURVE,
  T2_SURVIVAL_CURVE,
  T3_SURVIVAL_CURVE,
  INTERVENTION_TIMING_EFFECTS,
  calculateT1Survival,
  calculateT2Survival,
  calculateT3Survival,
  isGoldenHourAchievable,   // transport-only golden-hour check (restored; reported as analysis.evacuation.transport)
  TRANSPORT_THREAT_CONDITION,
  calculateGoldenHourBenefit,
  getRandomEvacuationTime,
  runSurvivalMonteCarlo
} from '../../constants/survivalCurves.js';

import {
  TOURNIQUET,
  TXA,
  HEMOSTATIC_DRESSING,
  NEEDLE_DECOMPRESSION,
  HYPOTHERMIA_PREVENTION,
  calculateTourniquetEffectiveness,
  calculateTXAEffectiveness,
  calculateHypothermiaMortalityMultiplier,
  getIndicatedInterventions,
  calculateCombinedInterventionEffect,
  NIGHT_FIRST_AID_MODIFIER,         // SVM-04: replaces local const (EXT-07)
  BASE_CLS_RESPONSE_MINUTES,         // SVM-05: replaces literal 3 (EXT-07)
  DOW_REDUCTION_RATES                // SVM-08: replaces literals 25/40 (EXT-07)
} from '../../constants/prehospitalInterventions.js';

import {
  TRIAGE_CATEGORIES,
  TRIAGE_OFFICER_PERFORMANCE,
  calculateTriageDistribution,
  adjustForMassCapacity,
  getTriageAccuracy
} from '../../constants/triageCategories.js';

import {
  EVACUATION_PARADIGMS,
  getParadigm,
  T1_SURVIVAL_BY_TIME,  // CALC-001/002: GOLDEN_HOUR.max_minutes (golden-hour limit)
  calculateEvacuationTime,   // paradigm-aware transit time (restored; reported as analysis.evacuation.transport)
  resolveEvacuationParadigm
} from '../../constants/evacuationParadigms.js';

// CALC-011: planner's terrain/weather
import { resolveScenarioEnvironment } from '../scenarioEnvironment.js';

import {
  BLOOD_ALERT_LEVELS,
  getBloodShortageDOWModifier
} from '../../constants/bloodProducts.js';

import {
  MTF_DEPLOYMENT_TIMES,
  POSTURE_MTF_STATUS,
  MEDICAL_CAPABILITY_DOW_MULTIPLIER,
  PROLONGED_FIELD_CARE,
  getDOWMultiplierFromCapability,
  getMTFStatusForPosture,
  calculateMedicalGap,
  calculateGapDOW
} from '../../constants/mtfDeploymentTime.js';

// ENH-039: BED_OCCUPANCY_MODEL thresholds for occupancy→capability mapping
import {
  BED_OCCUPANCY_MODEL
} from '../../constants/mtfCapabilities.js';

import {
  CLS_TRAINING_QUALITY as CLS_TRAINING_QUALITY_CANONICAL,  // SVM-02: canonical keys UNTRAINED/BASIC/STANDARD/ADVANCED
  TERRAIN_TYPES,                                            // .medical.evacuation_speed (restored; reported as analysis.evacuation.transport)
  THREAT_LEVELS                                             // SVM-06: .medical.first_aid_delay_minutes
} from '../../constants/operationsConfig.js';

// SVM-03: Terrain-based first aid time modifiers (richer data than operationsConfig TERRAIN_TYPES)
import {
  TERRAIN_TYPES as TERRAIN_TYPES_TF  // SVM-03: .time_modifier (first aid access time factor)
} from '../../constants/terrainFactors.js';

// SVM-07/09: Fallback constants from casualtyCalculatorConstants.js
import {
  T1_BASELINE_NO_INTERVENTION,    // SVM-07: 0.60 — baseline T1 survival without TCCC
  T1_SURVIVAL_FALLBACK_AT_30MIN,  // SVM-09: 0.95 — T1 NaN-guard starting value at 30min
  T1_SURVIVAL_DECAY_RATE_PER_MIN, // SVM-09: 0.005 — linear decay rate /min beyond 30min
  T1_SURVIVAL_FALLBACK_FLOOR,     // SVM-09: 0.50 — NaN-guard floor for T1 fallback
  T2_SURVIVAL_FALLBACK,           // SVM-09: 0.95 — T2 NaN-guard fallback
  NATO_BASE_DOW_RATE,             // SVM-09: 0.12 — NATO AJMedP-1 base DOW rate
  getParadigmDOWModifier          // CALC-003: paradigm -> CASUALTY_DISTRIBUTION.dow_modifiers
} from '../../constants/casualtyCalculatorConstants.js';

// WTD-01: Wound-type distribution — optional DOW refinement
// When casualtyCalculator passes woundDistribution + operationType in config,
// baseDOWRate is refined using wound-specific DOW rates (JTS JTTR) instead of
// the flat triage-level DOW contribution. If not provided, behaviour is unchanged.
import {
  getWeightedDOWRate
} from '../../constants/woundTypeDistribution.js';

// CALC-010: required-constant reader (throws with the path instead of a literal fallback)
import { requireNumber } from '../requireValue.js';
import { baseDowFromWounds } from './woundHazardModule.js';
import { deriveHazardCare } from './hazardCareModule.js';
import { PARADIGM_HOLD_HOURS, PREOP_INTERVAL_MIN, HOLD_HOURS_LIMIT } from '../../constants/woundHazardParameters.js';

// ============================================================================
// CONSTANTS - Derived from research, no hardcoding
// ============================================================================

// SVM-02 FIX (February 24, 2026): Local CLS_TRAINING_QUALITY deleted.
// CLS_TRAINING_QUALITY_CANONICAL imported from operationsConfig.js v2.2.
// Key adapter: legacy keys (EXCELLENT/GOOD/ADEQUATE/MINIMAL/NONE) → canonical
// (UNTRAINED/BASIC/STANDARD/ADVANCED). Field adapter: canonical has no
// .tourniquet_time_modifier — synthesized from .tourniquet_correct_rate using:
//   time_modifier = 2.0 - (tourniquet_correct_rate * 1.5)
// This maps correct_rate [0..1] to time_modifier [2.0..0.5] inverse relationship.
// Calibration vs local values: within 10% for all levels (UNTRAINED→1.70 vs local 2.0;
// ADVANCED→0.56 vs local 0.70 for EXCELLENT). Acceptable operational uncertainty.
const CLS_KEY_MAP = {
  // Legacy keys → canonical
  EXCELLENT: 'ADVANCED',
  GOOD:      'STANDARD',
  ADEQUATE:  'STANDARD',
  MINIMAL:   'BASIC',
  NONE:      'UNTRAINED',
  // Pass-through canonical keys
  UNTRAINED: 'UNTRAINED',
  BASIC:     'BASIC',
  STANDARD:  'STANDARD',
  ADVANCED:  'ADVANCED'
};
/**
 * Resolve CLS config from canonical, returning backward-compat shape.
 * Returns { tourniquet_time_modifier, txa_probability }.
 * @param {string} clsKey - Local or canonical CLS training quality key
 */
function resolveClsConfig(clsKey) {
  // 2026-10-10: an unknown key used to fall back to STANDARD silently (so 'POOR' behaved like 'ADEQUATE').
  // An unknown key now throws; the calculator always passes a key from resolveClsQuality().
  const canonicalKey = CLS_KEY_MAP[String(clsKey).toUpperCase()];
  if (!canonicalKey) {
    throw new Error(`survivalModule.resolveClsConfig: unknown CLS training key '${clsKey}' (valid: ${Object.keys(CLS_KEY_MAP).join(', ')})`);
  }
  const canonical = CLS_TRAINING_QUALITY_CANONICAL[canonicalKey];
  if (!canonical) {
    throw new Error(`survivalModule.resolveClsConfig: operationsConfig.CLS_TRAINING_QUALITY has no '${canonicalKey}'`);
  }
  // Synthesize time_modifier: inverse of tourniquet_correct_rate
  const tourniquet_time_modifier = 2.0 - (canonical.tourniquet_correct_rate * 1.5);
  return {
    tourniquet_time_modifier: Math.round(tourniquet_time_modifier * 100) / 100,
    txa_probability: canonical.txa_probability,
    tourniquet_correct_rate: canonical.tourniquet_correct_rate,
    survival_modifier: canonical.survival_modifier   // bonus field available to callers
  };
}
// Build legacy flat map for re-export compatibility
const CLS_TRAINING_QUALITY = Object.fromEntries(
  Object.keys(CLS_KEY_MAP).map(k => [k, resolveClsConfig(k)])
);

// SVM-03 FIX (February 24, 2026): Local TERRAIN_FIRST_AID_MODIFIERS deleted.
// TERRAIN_TYPES_TF imported from terrainFactors.js v1.1.0. Uses .time_modifier field.
// Key adapter maps legacy caller keys (OPEN, MARSH, RIVER, MIXED) to canonical.
// Local values: URBAN=1.2, MOUNTAIN=1.5, FOREST=1.3, OPEN=0.9, MARSH=1.6, RIVER=1.4, MIXED=1.0
// Canonical .time_modifier: URBAN=1.30, MOUNTAIN=1.50, FOREST=1.25, PLAINS(OPEN)=1.00,
//   SWAMP_WETLAND(MARSH/RIVER)=1.80, ROLLING_HILLS(MIXED)=1.15
// Calibration difference <12% (within operational uncertainty bounds).
const TERRAIN_FA_KEY_MAP = {
  OPEN:     'PLAINS',
  MARSH:    'SWAMP_WETLAND',
  RIVER:    'SWAMP_WETLAND',
  MIXED:    'ROLLING_HILLS',
  URBAN:    'URBAN',
  MOUNTAIN: 'MOUNTAIN',
  FOREST:   'FOREST',
  PLAINS:   'PLAINS',
  ROLLING_HILLS: 'ROLLING_HILLS',
  DESERT:   'DESERT',
  COASTAL:  'COASTAL',
  ARCTIC:   'ARCTIC',
  SWAMP_WETLAND: 'SWAMP_WETLAND'
};
// Build flat scalar map from canonical for re-export compatibility
// (survivalModule re-exports TERRAIN_FIRST_AID_MODIFIERS for consuming modules)
const TERRAIN_FIRST_AID_MODIFIERS = Object.fromEntries(
  Object.entries(TERRAIN_FA_KEY_MAP).map(([localKey, canonKey]) => [
    localKey,
    TERRAIN_TYPES_TF[canonKey]?.time_modifier ?? 1.0
  ])
);

// SVM-04: NIGHT_FIRST_AID_MODIFIER = 1.25 deleted — now imported from
// prehospitalInterventions.js (EXT-07). Value unchanged (1.25).
// Re-exported at bottom for backward compatibility.

// ============================================================================
// CORE FUNCTIONS
// ============================================================================

/**
 * Calculate time to first TCCC intervention (Platinum Period)
 * 
 * @param {Object} config - Configuration object
 * @param {string} config.terrain - Terrain type
 * @param {string} config.clsTraining - CLS training quality
 * @param {boolean} config.isNight - Whether night operation
 * @param {string} config.threatLevel - Enemy threat level
 * @returns {Object} First intervention timing analysis
 */
// 2026-10-18 (T3-01): read the blood alert level that Tab 3 saves (see calculateComprehensiveDOW).
const BLOOD_ALERT_FROM_TAB3 = true;

export function calculateTimeToFirstIntervention(config) {
  const {
    terrain = 'MIXED',
    clsTraining = 'ADEQUATE',
    isNight = false,
    threatLevel = 'MEDIUM'
  } = config;

  // Base time: average time for buddy to reach casualty
  // SVM-05: BASE_CLS_RESPONSE_MINUTES = 3 (from prehospitalInterventions.js EXT-07)
  let baseTimeMinutes = BASE_CLS_RESPONSE_MINUTES; // baseline minutes, ideal conditions

  // Apply terrain modifier
  const terrainMod = TERRAIN_FIRST_AID_MODIFIERS[terrain] || 1.0;
  let adjustedTime = baseTimeMinutes * terrainMod;

  // Apply CLS training modifier
  const clsConfig = resolveClsConfig(clsTraining);  // SVM-02 FIX: resolveClsConfig reads canonical
  adjustedTime *= clsConfig.tourniquet_time_modifier;

  // Apply night modifier
  if (isNight) {
    adjustedTime *= NIGHT_FIRST_AID_MODIFIER;
  }

  // Apply threat modifier (under fire = delayed care)
  // SVM-06 FIX: THREAT_LEVELS.medical.first_aid_delay_minutes from operationsConfig.js v2.2.
  // Key adapter maps local keys to canonical; INTENSE/PEER → EXTREME_THREAT (worst-case planning).
  const THREAT_DELAY_KEY_MAP = {
    LOW:      'LOW_THREAT',
    MEDIUM:   'MODERATE_THREAT',
    HIGH:     'HIGH_THREAT',
    INTENSE:  'EXTREME_THREAT',
    PEER:     'EXTREME_THREAT',
    LOW_THREAT:      'LOW_THREAT',
    MODERATE_THREAT: 'MODERATE_THREAT',
    HIGH_THREAT:     'HIGH_THREAT',
    EXTREME_THREAT:  'EXTREME_THREAT'
  };
  const canonicalThreatKey = THREAT_DELAY_KEY_MAP[threatLevel?.toUpperCase()] || 'MODERATE_THREAT';
  const threatDelayMinutes = THREAT_LEVELS[canonicalThreatKey]?.medical?.first_aid_delay_minutes ?? 2;
  adjustedTime += threatDelayMinutes;

  // Check platinum period achievement
  const platinumAchieved = adjustedTime <= 10;
  const platinumSurvivalBonus = platinumAchieved ?
    requireNumber(SURVIVAL_TIME_WINDOWS?.PLATINUM_10?.survival_bonus,
      'survivalModule.calculateTimeToFirstIntervention', 'SURVIVAL_TIME_WINDOWS.PLATINUM_10.survival_bonus') : 0;

  return {
    time_to_first_intervention_minutes: Math.round(adjustedTime * 10) / 10,
    platinum_achieved: platinumAchieved,
    platinum_survival_bonus: platinumSurvivalBonus,
    modifiers_applied: {
      terrain: terrainMod,
      cls_training: clsConfig.tourniquet_time_modifier,
      night: isNight ? NIGHT_FIRST_AID_MODIFIER : 1.0,
      threat_delay_minutes: threatDelayMinutes  // SVM-06 FIX
    },
    cls_quality: clsTraining,
    tourniquet_correct_rate: clsConfig.tourniquet_correct_rate,
    txa_administration_probability: clsConfig.txa_probability
  };
}

/**
 * Calculate TCCC intervention effects on survival
 * 
 * This is the CRITICAL function that was missing from v3.1
 * Tourniquet timing: 87% survival at <5min vs 28% at >30min
 * TXA timing: 32% mortality reduction at <1h vs 8% INCREASE at >3h
 * 
 * @param {Object} config - Configuration object
 * @param {number} config.timeToIntervention - Minutes to first intervention
 * @param {number} config.timeToRole2 - Minutes to Role 2
 * @param {string} config.clsTraining - CLS training quality
 * @param {number} config.ambientTempC - Ambient temperature in Celsius
 * @param {string} config.woundMechanism - Wound mechanism type
 * @returns {Object} TCCC intervention analysis
 */
export function calculateTCCCInterventionEffects(config) {
  const {
    timeToIntervention = 5,
    timeToRole2 = 90,
    clsTraining = 'ADEQUATE',
    ambientTempC = 15,
    woundMechanism = 'PENETRATING'
  } = config;

  const clsConfig = resolveClsConfig(clsTraining);  // SVM-02 FIX: resolveClsConfig reads canonical

  // Estimate time to Role 1 (intervention time + travel to Role 1)
  // Better CLS training reduces this time
  const clsTimeModifier = clsConfig?.time_modifier || 1.0;
  const estimatedTimeToRole1 = Math.round((timeToIntervention + 15) * clsTimeModifier);

  // Check if PFC (Prolonged Field Care) will be needed
  const pfcThresholdMinutes = requireNumber(PROLONGED_FIELD_CARE?.activation_threshold_minutes,
    'survivalModule.calculateTCCCInterventionEffects', 'PROLONGED_FIELD_CARE.activation_threshold_minutes');
  const pfcRequired = timeToRole2 > pfcThresholdMinutes;
  const pfcDuration = pfcRequired ? timeToRole2 - pfcThresholdMinutes : 0;

  // 1. TOURNIQUET EFFECTIVENESS
  // Uses calculateTourniquetEffectiveness from prehospitalInterventions.js
  let tourniquetSurvival = 1.0;
  let tourniquetApplied = false;

  // Check if tourniquet indicated for this mechanism
  const indicatedInterventions = getIndicatedInterventions ? 
    getIndicatedInterventions(woundMechanism) : ['TOURNIQUET', 'TXA'];

  if (indicatedInterventions.includes('TOURNIQUET')) {
    tourniquetSurvival = calculateTourniquetEffectiveness(timeToIntervention);
    tourniquetApplied = true;
  }

  // 2. TXA EFFECTIVENESS
  // Must be given within 3 hours, ideally within 1 hour
  let txaEffect = 1.0;
  let txaAdministered = false;
  const txaTimeMinutes = timeToIntervention + 2; // +2 min for administration

  // Probability of TXA being administered based on CLS training
  if (Math.random() < clsConfig.txa_probability) {
    txaEffect = calculateTXAEffectiveness(txaTimeMinutes);
    txaAdministered = true;
  }

  // 3. HYPOTHERMIA EFFECT
  // Each degree below 35Â°C increases mortality by 8%
  let hypothermiaMultiplier = 1.0;
  let hypothermiaRisk = 'LOW';

  if (ambientTempC < 35) {
    hypothermiaMultiplier = calculateHypothermiaMortalityMultiplier(ambientTempC);
    
    if (ambientTempC < 10) hypothermiaRisk = 'CRITICAL';
    else if (ambientTempC < 20) hypothermiaRisk = 'HIGH';
    else if (ambientTempC < 30) hypothermiaRisk = 'MODERATE';
  }

  // 4. COMBINED EFFECT
  // Base survival without interventions would be lower
  // Interventions improve survival multiplicatively
  // SVM-07 FIX: T1_BASELINE_NO_INTERVENTION imported from casualtyCalculatorConstants.js v2.2.
  const baselineNoIntervention = T1_BASELINE_NO_INTERVENTION;
  
  // Calculate survival improvement from interventions
  const tourniquetImprovement = tourniquetApplied ? 
    (tourniquetSurvival - baselineNoIntervention) / (1 - baselineNoIntervention) : 0;
  
  // TXA reduces mortality (not improves survival directly)
  // txaEffect < 1.0 means mortality reduction, > 1.0 means mortality increase
  // TCCC FIX: Extract numeric value if object returned
  const txaMortalityModifier = txaAdministered ? 
    (typeof txaEffect === 'object' ? (txaEffect.rate || txaEffect.modifier || 1.0) : txaEffect) : 1.0;

  // Use imported calculateCombinedInterventionEffect if available
  let combinedSurvivalModifier;
  let combinedFromConstants = false;
  
  // TCCC FIX: Extract numeric value from hypothermiaMultiplier if object
  const hypothermiaNumeric = typeof hypothermiaMultiplier === 'object' ? 
    (hypothermiaMultiplier.multiplier || hypothermiaMultiplier.modifier || 1.0) : hypothermiaMultiplier;
  
  if (calculateCombinedInterventionEffect) {
    const combinedResult = calculateCombinedInterventionEffect({
      tourniquet: tourniquetApplied ? { applied: true, time_minutes: timeToIntervention } : null,
      txa: txaAdministered ? { administered: true, time_minutes: txaTimeMinutes } : null,
      hypothermia: { ambient_temp_c: ambientTempC }
    });
    
    // TCCC FIX: Use survival_rate (not survival_modifier) from constants function
    combinedSurvivalModifier = combinedResult?.survival_modifier || combinedResult?.survival_rate ||
      (tourniquetApplied ? 
        (tourniquetSurvival / baselineNoIntervention) * (1 / txaMortalityModifier) * (1 / hypothermiaNumeric) :
        (1 / txaMortalityModifier) * (1 / hypothermiaNumeric));
    combinedFromConstants = true;
  } else {
    // Fallback to local calculation
    combinedSurvivalModifier = tourniquetApplied ? 
      (tourniquetSurvival / baselineNoIntervention) * (1 / txaMortalityModifier) * (1 / hypothermiaNumeric) :
      (1 / txaMortalityModifier) * (1 / hypothermiaNumeric);
  }

  // Calculate DOW reduction from TCCC
  // Good TCCC can reduce DOW by 40-60%
  // SVM-08: 25 → DOW_REDUCTION_RATES.TOURNIQUET_ONLY * 100 (= 25, from prehospitalInterventions.js EXT-07)
  const dowReductionPercent = Math.min(60, Math.max(0,
    (tourniquetApplied ? Math.round(DOW_REDUCTION_RATES.TOURNIQUET_ONLY * 100) : 0) +
    (txaAdministered && txaEffect < 1.0 ? Math.round((1 - txaEffect) * 100) : 0) -
    (hypothermiaMultiplier > 1.0 ? Math.round((hypothermiaMultiplier - 1) * 100) : 0)
  ));

  return {
    tourniquet: {
      applied: tourniquetApplied,
      time_minutes: tourniquetApplied ? timeToIntervention : null,
      survival_rate: tourniquetApplied ? Math.round(tourniquetSurvival * 100) : null,
      improvement_percent: Math.round(tourniquetImprovement * 100),
      // Educational data from imported constants
      timing_effects: INTERVENTION_TIMING_EFFECTS?.TOURNIQUET || null,
      constant_data: TOURNIQUET || null,
      educational_note: tourniquetApplied ?
        `Tourniquet at ${timeToIntervention}min: ${Math.round(tourniquetSurvival * 100)}% survival` +
        ` (vs 28% at >30min)` : 'Tourniquet not indicated for this mechanism'
    },
    txa: {
      administered: txaAdministered,
      time_minutes: txaAdministered ? txaTimeMinutes : null,
      mortality_modifier: txaAdministered ? Math.round(txaEffect * 100) / 100 : null,
      // Educational data from imported constants
      timing_effects: INTERVENTION_TIMING_EFFECTS?.TXA || null,
      constant_data: TXA || null,
      effect_description: txaAdministered ?
        (txaEffect < 1.0 ? `${Math.round((1 - txaEffect) * 100)}% mortality reduction` :
         txaEffect > 1.0 ? `${Math.round((txaEffect - 1) * 100)}% mortality INCREASE (late administration)` :
         'No effect') : 'TXA not administered'
    },
    hypothermia: {
      ambient_temp_c: ambientTempC,
      risk_level: hypothermiaRisk,
      mortality_multiplier: Math.round(hypothermiaMultiplier * 100) / 100,
      additional_mortality_percent: Math.round((hypothermiaMultiplier - 1) * 100),
      // Educational data from imported constants
      constant_data: HYPOTHERMIA_PREVENTION || null
    },
    // Other intervention constants for educational context
    other_interventions: {
      hemostatic_dressings: HEMOSTATIC_DRESSING || null,
      needle_decompression: NEEDLE_DECOMPRESSION || null
    },
    // PFC (Prolonged Field Care) analysis using timeToRole2
    prolonged_field_care: {
      time_to_role2_minutes: timeToRole2,
      estimated_time_to_role1: estimatedTimeToRole1,
      pfc_required: pfcRequired,
      pfc_duration_minutes: pfcDuration,
      pfc_threshold: pfcThresholdMinutes,
      pfc_protocols: PROLONGED_FIELD_CARE || null,
      educational_note: pfcRequired ? 
        `PFC required: ${pfcDuration} min beyond golden hour. Enhanced monitoring and interventions needed.` :
        'Standard evacuation timeline - PFC not required'
    },
    combined: {
      survival_modifier: Math.round(combinedSurvivalModifier * 100) / 100,
      dow_reduction_percent: dowReductionPercent,
      calculation_source: combinedFromConstants ? 'CONSTANTS' : 'LOCAL',
      tccc_quality_assessment: assessTCCCQuality(tourniquetApplied, txaAdministered, 
        timeToIntervention, hypothermiaRisk)
    }
  };
}

/**
 * Assess overall TCCC quality
 */
function assessTCCCQuality(tourniquetApplied, txaAdministered, timeToIntervention, hypothermiaRisk) {
  let score = 0;
  
  if (tourniquetApplied && timeToIntervention <= 5) score += 40;
  else if (tourniquetApplied && timeToIntervention <= 15) score += 25;
  else if (tourniquetApplied) score += 10;
  
  if (txaAdministered) score += 30;
  
  if (hypothermiaRisk === 'LOW') score += 30;
  else if (hypothermiaRisk === 'MODERATE') score += 20;
  else if (hypothermiaRisk === 'HIGH') score += 10;
  
  if (score >= 80) return 'EXCELLENT';
  if (score >= 60) return 'GOOD';
  if (score >= 40) return 'ADEQUATE';
  if (score >= 20) return 'POOR';
  return 'CRITICAL';
}

/**
 * Calculate triage officer performance degradation
 * 
 * Ukraine 2022-2024 data shows significant accuracy loss:
 * - OPTIMAL: 95% accuracy
 * - FATIGUED (>4h): 82% accuracy
 * - MASS_CASUALTY: 70% accuracy
 * - OVERWHELMED: 55% accuracy
 * 
 * @param {Object} config - Configuration object
 * @param {number} config.hoursOnShift - Hours triage officer has been working
 * @param {number} config.dailyCasualties - Casualties received today
 * @param {number} config.triageCapacity - Triage capacity per shift
 * @param {string} config.experienceLevel - Officer experience level
 * @returns {Object} Triage performance analysis
 */
export function calculateTriageOfficerPerformance(config) {
  const {
    hoursOnShift = 2,
    dailyCasualties = 30,
    triageCapacity = 50,
    experienceLevel = 'EXPERIENCED'
  } = config;

  // Determine performance condition
  const utilizationRatio = dailyCasualties / triageCapacity;
  let condition = 'OPTIMAL';

  if (hoursOnShift > 4 && utilizationRatio > 1.0) {
    condition = 'OVERWHELMED';
  } else if (utilizationRatio > 1.0) {
    condition = 'MASS_CASUALTY';
  } else if (hoursOnShift > 4) {
    condition = 'FATIGUED';
  }

  // Get performance data from constants
  const perfData = TRIAGE_OFFICER_PERFORMANCE?.[condition] || {
    accuracy: 0.82,
    undertriage_rate: 0.10,
    overtriage_rate: 0.08
  };

  // Adjust for experience
  const experienceModifiers = {
    EXPERT: { accuracy: 1.05, undertriage: 0.8 },
    EXPERIENCED: { accuracy: 1.0, undertriage: 1.0 },
    COMPETENT: { accuracy: 0.95, undertriage: 1.1 },
    NOVICE: { accuracy: 0.85, undertriage: 1.3 }
  };

  const expMod = experienceModifiers[experienceLevel] || experienceModifiers.COMPETENT;

  const adjustedAccuracy = Math.min(0.98, perfData.accuracy * expMod.accuracy);
  const adjustedUndertriage = perfData.undertriage_rate * expMod.undertriage;

  // Calculate impact on DOW
  // Undertriage (T1 marked as T2) = delayed surgery = higher mortality
  const undertriageT1Count = Math.round(dailyCasualties * 0.25 * adjustedUndertriage); // 25% are T1
  const delayedSurgeryMortalityIncrease = 0.15; // 15% additional mortality per misclassified T1
  const additionalDeaths = Math.round(undertriageT1Count * delayedSurgeryMortalityIncrease);

  return {
    condition,
    performance: {
      accuracy_percent: Math.round(adjustedAccuracy * 100),
      undertriage_rate: Math.round(adjustedUndertriage * 100),
      overtriage_rate: Math.round(perfData.overtriage_rate * 100)
    },
    factors: {
      hours_on_shift: hoursOnShift,
      utilization_ratio: Math.round(utilizationRatio * 100),
      experience_level: experienceLevel
    },
    impact: {
      misclassified_t1_count: undertriageT1Count,
      additional_deaths_from_undertriage: additionalDeaths,
      dow_modifier: 1 + (additionalDeaths / Math.max(1, dailyCasualties * 0.25))
    },
    recommendations: generateTriageRecommendations(condition, hoursOnShift, utilizationRatio)
  };
}

/**
 * Generate triage recommendations
 */
function generateTriageRecommendations(condition, hours, utilization) {
  const recs = [];
  
  if (hours > 4) {
    recs.push('Rotate triage officers - fatigue degrading accuracy');
  }
  if (utilization > 1.0) {
    recs.push('MASCAL protocols needed - capacity exceeded');
    recs.push('Consider simplified START/SALT triage');
  }
  if (utilization > 1.5) {
    recs.push('CRITICAL: Request additional triage personnel');
  }
  if (condition === 'OVERWHELMED') {
    recs.push('Implement crisis standards of care');
    recs.push('Pre-triage at point of injury recommended');
  }
  
  return recs;
}

/**
 * Calculate comprehensive DOW (Died of Wounds) rate
 * 
 * Integrates all survival factors:
 * - TCCC interventions
 * - Platinum 10 minutes
 * - Golden hour
 * - MTF capability
 * - Blood supply
 * - Surgical capacity
 * - Triage accuracy
 * 
 * @param {Object} config - Complete configuration
 * @returns {Object} DOW analysis with rate and breakdown
 */
export function calculateComprehensiveDOW(config) {
  const {
    brigade,
    scenario,
    medicalConfig = {},
    medevacAnalysis = null,  // Pre-calculated MEDEVAC analysis (recommended_method is required)
    timeToSurgeryMinutes,    // CALC-001/002: cumulative minutes from injury to surgery (required)
    tcccAnalysis: providedTcccAnalysis = null,  // Use pre-calculated TCCC if provided
    dailyCasualties = 50,
    day = 1,
    // WTD-01: Optional wound-type distribution inputs from casualtyCalculator.
    // When provided, baseDOWRate is refined using wound-specific JTS JTTR DOW rates.
    // When null/undefined, behaviour is identical to pre-WTD-01 (zero regression risk).
    woundDistribution = null,
    operationType = null,
    // Wound-type hazard model (docs/planning/PLAN_WOUND_HAZARD_IMPL.md step 5). null = OFF (identical to before).
    // { mode: 'SHADOW' | 'ON', arrivalHours, antibioticsInStock, dressingsInStock }.
    // SHADOW computes and reports the hazard base DOW next to the flat one without changing the result;
    // ON replaces the base DOW. ON is NOT complete until the double-counting guard (plan step 6) is done.
    woundHazard = null,
    // ENH-039: Running R2B bed occupancy % from casualtyCalculator day loop.
    // 0 = no data (no effect). Positive value → bed shortage → higher DOW rate.
    bedOccupancyPct = 0
  } = config;

  // 1. Get evacuation paradigm - use imported function if available
  // CALC-005: one resolver for the paradigm; a scenario saved before Tab 2 wrote the field gets
  // the documented default and the run reports it (survivalModule._paradigmDefaulted -> warning).
  const { paradigm, defaulted: paradigmDefaulted } = resolveEvacuationParadigm(scenario);
  if (paradigmDefaulted) {
    survivalModule._paradigmDefaulted = true;
  }

  const paradigmLookupResult = getParadigm ? getParadigm(paradigm) : EVACUATION_PARADIGMS?.[paradigm];
  
  // v4.4: ZERO TOLERANCE - No fallback object
  // If lookup fails, this is a constants file bug that MUST be fixed
  if (!paradigmLookupResult) {
    console.error(`[CRITICAL] evacuationParadigms.js missing paradigm "${paradigm}" - constants file BUG!`);
    // INC-002: Set module-level flag so casualtyCalculator can add a visible warning to results.
    // Fall back to CONTESTED so the calculation can produce a result (not undefined/NaN).
    survivalModule._paradigmMissing = paradigm;
  }

  const paradigmConfig = paradigmLookupResult
    || getParadigm?.('CONTESTED')
    || EVACUATION_PARADIGMS?.['CONTESTED'];

  // 2. Calculate time to first intervention (Platinum Period)
  const firstInterventionAnalysis = calculateTimeToFirstIntervention({
    terrain: brigade?.environment?.terrain || 'MIXED',
    clsTraining: brigade?.cls_training || scenario?.force_quality?.cls_training || 'ADEQUATE',
    isNight: scenario?.operation_parameters?.time_of_day === 'NIGHT',
    threatLevel: brigade?.environment?.threat_level || 'MEDIUM'
  });

  // 3. Use provided TCCC analysis OR calculate fresh
  const tcccAnalysis = providedTcccAnalysis || calculateTCCCInterventionEffects({
    timeToIntervention: firstInterventionAnalysis.time_to_first_intervention_minutes,
    // F-02 SITE 1 FIX: Tab3 saves role2_distance_km, not role2.
    // mtf_distances.role2 does not exist → condition was always falsy → timeToRole2 always 90.
    // Effect: pfcRequired = 90 > 60 = always true → PFC permanently activated for ALL casualties.
    // Fix: read role2_distance_km. Use != null (not ||) so a configured distance of 0 is accepted.
    timeToRole2: medicalConfig?.mtf_distances?.role2_distance_km != null ?
      (medicalConfig.mtf_distances.role2_distance_km * 4) + 20 : 90,
    clsTraining: brigade?.cls_training || 'ADEQUATE',
    ambientTempC: resolveInput(scenario?.environment?.temperature_c, 'ambient_temperature_c', { zeroIsMissing: true }),
    woundMechanism: 'PENETRATING'
  });

  // 4. Time to surgery and golden hour.
  // CALC-001/002 (2026-10-09): the T1 survival curve is defined on the time from injury to the start
  // of surgery. The caller supplies the cumulative time-to-surgery it already models (casualty
  // collection, Role 1 treatment, transport, forward surgery where present). Previously this block
  // took a transit time from the MEDEVAC analysis and, whenever the Phase 2.5 evacuation analysis
  // replaced that analysis, silently used a fixed 90 minutes; a second multiplier then applied the
  // real time on top. Time now enters once, through the curve.
  const evacuationTimeMinutes = requireNumber(timeToSurgeryMinutes,
    'survivalModule.calculateComprehensiveDOW', 'config.timeToSurgeryMinutes (cumulative time from injury to surgery)');
  if (evacuationTimeMinutes <= 0) {
    throw new Error(`survivalModule.calculateComprehensiveDOW: config.timeToSurgeryMinutes must be > 0 (got: ${evacuationTimeMinutes})`);
  }
  const goldenHourMinutes = requireNumber(T1_SURVIVAL_BY_TIME?.GOLDEN_HOUR?.max_minutes,
    'survivalModule.calculateComprehensiveDOW', 'T1_SURVIVAL_BY_TIME.GOLDEN_HOUR.max_minutes');
  const goldenHourAchieved = evacuationTimeMinutes <= goldenHourMinutes;
  if (typeof medevacAnalysis?.recommended_method !== 'string') {
    throw new Error('survivalModule.calculateComprehensiveDOW: medevacAnalysis.recommended_method is absent. ' +
      'Path: casualtyCalculator medevacAnalysis (calculateComprehensiveMEDEVAC)');
  }
  const evacuationMethod = medevacAnalysis.recommended_method;
  let goldenHourBenefitData = null;

  const posture = brigade?.operation?.operational_posture || 'DELIBERATE_DEFENSE';
  const mtfStatus = getMTFStatusForPosture(posture);
  // F-02 SITE 2 FIX: Tab3 saves role2_distance_km, not role2. Uses ?? so a configured 0 is accepted.
  // Reported in the output and used by the survival Monte Carlo below.
  const role2DistanceKm = resolveInput(medicalConfig?.mtf_distances?.role2_distance_km, 'role2_distance_km');

  // Environment for the survival Monte Carlo evacuation-time sampler
  const environment = {
    terrain: brigade?.environment?.terrain || 'MIXED',
    weather: resolveScenarioEnvironment(scenario).weather,
    threat_level: brigade?.environment?.threat_level || 'MEDIUM'
  };

  // Calculate golden hour benefit using imported function
  if (calculateGoldenHourBenefit) {
    goldenHourBenefitData = calculateGoldenHourBenefit(evacuationTimeMinutes);
  }

  // Transport-only view of the evacuation (restored 2026-10-09): what the paradigm model expects for the
  // direct transit to Role 2, whether that transit alone fits the golden hour, and the terrain speed factor.
  // Reported for comparison with the full time to surgery above; it does not change the DOW.
  const paradigmTransit = calculateEvacuationTime(paradigmConfig, role2DistanceKm, environment);
  const goldenHourTransport = isGoldenHourAchievable(
    role2DistanceKm,
    evacuationMethod === 'AIR_PRIMARY' ? 'HELICOPTER' : 'GROUND',
    { threatLevel: TRANSPORT_THREAT_CONDITION[brigade?.environment?.threat_level] }
  );
  const terrainEvacuationSpeed = requireNumber(TERRAIN_TYPES?.[environment.terrain]?.medical?.evacuation_speed,
    'survivalModule.calculateComprehensiveDOW', `operationsConfig.TERRAIN_TYPES.${environment.terrain}.medical.evacuation_speed`);

  // 5. Get triage performance - use imported functions if available
  let triageDistributionData = null;
  let triageAccuracyData = null;
  
  if (calculateTriageDistribution) {
    triageDistributionData = calculateTriageDistribution(dailyCasualties, 
      scenario?.operation_parameters?.operation_type || 'BALANCED');
  }
  
  if (getTriageAccuracy) {
    const hoursOnShift = (day - 1) % 3 * 4 + 2;
    triageAccuracyData = getTriageAccuracy(hoursOnShift, dailyCasualties / 24);
  }
  
  // Check if MASCAL adjustment needed
  const isMascal = dailyCasualties > 100;
  let adjustedTriageDistribution = triageDistributionData;
  if (isMascal && adjustForMassCapacity && triageDistributionData) {
    adjustedTriageDistribution = adjustForMassCapacity(triageDistributionData, true);
  }
  
  const triagePerformance = calculateTriageOfficerPerformance({
    hoursOnShift: (day - 1) % 3 * 4 + 2, // Rotate every 4 hours
    dailyCasualties,
    triageCapacity: 50,
    experienceLevel: scenario?.force_quality?.medical_experience || 'EXPERIENCED'
  });

  // 6. Get base survival rates from constants
  // BUG-057 FIX: Add defensive checks with NATO-accurate fallback calculations
  // Fallback formula based on NATO medical doctrine (AJMedP-1, ATP 4-02):
  // - T1 survival drops ~0.5% per minute beyond 30 min (golden hour critical)
  // - T2 survival relatively stable at 94-96% with timely evacuation
  // - T3 survival >98% (minor injuries)
  
  let t1Baseline;
  if (typeof calculateT1Survival === 'function') {
    t1Baseline = calculateT1Survival(evacuationTimeMinutes);
  }
  // BUG-057: Guard against NaN/undefined from survival curve function
  // SVM-09 FIX: All literals replaced with named constants from CCC (February 24, 2026).
  if (t1Baseline === undefined || t1Baseline === null || isNaN(t1Baseline)) {
    const minutesBeyond30 = Math.max(0, evacuationTimeMinutes - 30);
    t1Baseline = Math.max(
      T1_SURVIVAL_FALLBACK_FLOOR,                                            // SVM-09: 0.50
      T1_SURVIVAL_FALLBACK_AT_30MIN - (minutesBeyond30 * T1_SURVIVAL_DECAY_RATE_PER_MIN) // SVM-09: 0.95 - min*0.005
    );
    console.warn(`[BUG-057 FIX] calculateT1Survival returned invalid value, using NATO fallback: ${(t1Baseline * 100).toFixed(1)}% for ${evacuationTimeMinutes}min evac`);
  }
  
  let t2Baseline;
  if (typeof calculateT2Survival === 'function') {
    t2Baseline = calculateT2Survival(evacuationTimeMinutes / 60);
  }
  // BUG-057: Guard against NaN/undefined
  // SVM-09 FIX: 0.95 replaced with T2_SURVIVAL_FALLBACK from CCC (February 24, 2026).
  if (t2Baseline === undefined || t2Baseline === null || isNaN(t2Baseline)) {
    t2Baseline = T2_SURVIVAL_FALLBACK;  // SVM-09: 0.95 (NATO fallback, mid-range 6-12h window)
    console.warn(`[BUG-057 FIX] calculateT2Survival returned invalid value, using NATO fallback: ${(T2_SURVIVAL_FALLBACK * 100).toFixed(0)}%`);
  }
  
  let t3Baseline;
  if (typeof calculateT3Survival === 'function') {
    t3Baseline = calculateT3Survival(evacuationTimeMinutes / 60);
  }
  // BUG-057: Guard against NaN/undefined
  // SVM-09 FIX: 0.98 replaced with T3_SURVIVAL_CURVE.survival_windows.under_72_hours.rate
  // (= 0.98 — exact canonical match). No new constant needed (February 24, 2026).
  if (t3Baseline === undefined || t3Baseline === null || isNaN(t3Baseline)) {
    t3Baseline = requireNumber(T3_SURVIVAL_CURVE?.survival_windows?.under_72_hours?.rate, 'survivalModule.js:970', 'T3_SURVIVAL_CURVE.survival_windows.under_72_hours.rate');  // SVM-09
    console.warn(`[BUG-057 FIX] calculateT3Survival returned invalid value, using NATO fallback: ${((requireNumber(T3_SURVIVAL_CURVE?.survival_windows?.under_72_hours?.rate, 'survivalModule.js:971', 'T3_SURVIVAL_CURVE.survival_windows.under_72_hours.rate')) * 100).toFixed(0)}%`);
  }

  // 7. Get triage percentages
  // CALC-010: required constants, no literal fallbacks (APP_RULES 6.1)
  const _dowFn = 'survivalModule.calculateComprehensiveDOW';
  const t1Percent = requireNumber(TRIAGE_CATEGORIES?.T1_IMMEDIATE?.baseline_percentage, _dowFn, 'TRIAGE_CATEGORIES.T1_IMMEDIATE.baseline_percentage');
  const t2Percent = requireNumber(TRIAGE_CATEGORIES?.T2_DELAYED?.baseline_percentage, _dowFn, 'TRIAGE_CATEGORIES.T2_DELAYED.baseline_percentage');
  const t3Percent = requireNumber(TRIAGE_CATEGORIES?.T3_MINIMAL?.baseline_percentage, _dowFn, 'TRIAGE_CATEGORIES.T3_MINIMAL.baseline_percentage');
  const t4Percent = requireNumber(TRIAGE_CATEGORIES?.T4_EXPECTANT?.baseline_percentage, _dowFn, 'TRIAGE_CATEGORIES.T4_EXPECTANT.baseline_percentage');

  // Get mortality rates
  const t3Mortality = requireNumber(TRIAGE_CATEGORIES?.T3_MINIMAL?.mortality_rate, _dowFn, 'TRIAGE_CATEGORIES.T3_MINIMAL.mortality_rate');
  const t4Mortality = requireNumber(TRIAGE_CATEGORIES?.T4_EXPECTANT?.mortality_rate, _dowFn, 'TRIAGE_CATEGORIES.T4_EXPECTANT.mortality_rate');

  // 8. Apply TCCC modifier to T1 survival
  // BUG-057 FIX: Guard against NaN in TCCC analysis
  const tcccSurvivalModifier = (tcccAnalysis?.combined?.survival_modifier !== undefined && 
                                tcccAnalysis?.combined?.survival_modifier !== null &&
                                !isNaN(tcccAnalysis?.combined?.survival_modifier))
    ? tcccAnalysis.combined.survival_modifier
    : 1.0;  // Default: no modification if TCCC data unavailable
  
  if (tcccSurvivalModifier !== tcccAnalysis?.combined?.survival_modifier) {
    console.warn(`[BUG-057 FIX] tcccAnalysis.combined.survival_modifier was invalid (${tcccAnalysis?.combined?.survival_modifier}), using default 1.0`);
  }
  
  const t1WithTCCC = Math.min(0.98, t1Baseline * tcccSurvivalModifier);

  // 9. Calculate DOW contributions by category
  // T3 uses mortality rate directly (most T3 survive regardless of time)
  // But include t3Baseline for educational tracking
  const t1DOWContribution = t1Percent * (1 - t1WithTCCC);
  const t2DOWContribution = t2Percent * (1 - t2Baseline);
  const t3DOWContribution = t3Percent * t3Mortality;
  const t4DOWContribution = t4Percent * t4Mortality;

  let baseDOWRate = t1DOWContribution + t2DOWContribution + t3DOWContribution + t4DOWContribution;
  
  // BUG-057 FIX: Final guard - if baseDOWRate is still NaN, use NATO standard
  // SVM-09 DEFERRED: 0.12 (12% NATO standard DOW rate) should come from a named
  // constant e.g. NATO_STANDARD_DOW_RATE in surgicalCapacity.js or a new constants file.
  // No canonical source confirmed yet.
  // SVM-09 FIX: 0.12 replaced with NATO_BASE_DOW_RATE from CCC (February 24, 2026).
  if (isNaN(baseDOWRate) || baseDOWRate === undefined || baseDOWRate === null) {
    baseDOWRate = NATO_BASE_DOW_RATE;  // SVM-09: 0.12 — NATO AJMedP-1 standard
    console.warn(`[BUG-057 FIX] baseDOWRate was NaN, using NATO standard ${(NATO_BASE_DOW_RATE * 100).toFixed(0)}%`);
  }
  
  // BUG-057 FIX: Sanity check - DOW rate should be between 2% and 45%
  if (baseDOWRate < 0.02 || baseDOWRate > 0.45) {
    console.warn(`[BUG-057 FIX] baseDOWRate ${(baseDOWRate * 100).toFixed(1)}% outside expected range (2-45%), clamping`);
    baseDOWRate = Math.min(0.45, Math.max(0.02, baseDOWRate));
  }

  // WTD-01: Optional wound-type DOW refinement.
  // When casualtyCalculator provides woundDistribution + operationType, replace flat
  // triage-level DOW contributions with wound-type-weighted rates from JTS JTTR.
  // T3 and T4 contributions are unchanged — wound type does not significantly alter their rates.
  // Refinement is additive: all downstream modifiers (MTF, blood, triage, paradigm) still apply.
  let woundTypeApplied = false;
  if (woundDistribution && operationType) {
    try {
      const woundWeightedT1DOW = getWeightedDOWRate(operationType, 'T1', woundDistribution);
      const woundWeightedT2DOW = getWeightedDOWRate(operationType, 'T2', woundDistribution);
      const refinedBase =
        (t1Percent * woundWeightedT1DOW) +
        (t2Percent * woundWeightedT2DOW) +
        t3DOWContribution +
        t4DOWContribution;
      if (!isNaN(refinedBase) && refinedBase > 0) {
        baseDOWRate = refinedBase;
        woundTypeApplied = true;
      }
    } catch (e) {
      // Wound distribution refinement failed — continue with flat triage-level baseDOWRate
      console.warn('[WTD-01] Wound-type DOW refinement failed, using flat triage rates:', e.message);
    }
  }

  // Wound-type hazard model: time-dependent DOW by wound region (docs/documentation/SPEC_WOUND_HAZARD.md).
  // The flat base (above) is always kept as `baseDOWFlat` so both are reported.
  const baseDOWFlat = baseDOWRate;
  let woundHazardReport = null;
  if (woundHazard) {
    if (woundHazard.mode !== 'SHADOW' && woundHazard.mode !== 'ON') {
      throw new Error(`survivalModule.calculateComprehensiveDOW: woundHazard.mode must be 'SHADOW' or 'ON' (got: ${woundHazard.mode})`);
    }
    if (!woundDistribution) {
      throw new Error('survivalModule.calculateComprehensiveDOW: woundHazard requires woundDistribution');
    }
    // 2026-10-18 (Tab 3 review T3-01): Tab 3 saves the blood alert level (derived there from the stock and the days of supply) as
    // blood_config.alert_level; this read looked for blood_config.alert.current_level, a key nobody writes, so a red alert never
    // reached the survival model. BLOOD_ALERT_FROM_TAB3 = false restores the former read.
    const _bloodAlert = BLOOD_ALERT_FROM_TAB3
      ? (medicalConfig?.blood_config?.alert?.current_level || medicalConfig?.blood_config?.alert_level || 'GREEN')
      : (medicalConfig?.blood_config?.alert?.current_level || 'GREEN');
    const _hzBlood = getBloodShortageDOWModifier
      ? getBloodShortageDOWModifier(_bloodAlert)
      : { modifier: 1.0 };
    const _hzBloodMod = typeof _hzBlood === 'object' ? _hzBlood.modifier : _hzBlood;
    const _mcTail = scenario?.operation_parameters?.monte_carlo_modifiers;   // R2-24d: Monte Carlo tail parameters (absent = central values)
    const _hzCareBase = deriveHazardCare({
      controlTimeMin: firstInterventionAnalysis.time_to_first_intervention_minutes,
      // Tourniquet success of this force's training relative to the STANDARD level (Tab 1 CLS training).
      trainingScale: firstInterventionAnalysis.tourniquet_correct_rate /
        requireNumber(CLS_TRAINING_QUALITY_CANONICAL?.STANDARD?.tourniquet_correct_rate,
          'survivalModule.calculateComprehensiveDOW', 'operationsConfig.CLS_TRAINING_QUALITY.STANDARD.tourniquet_correct_rate'),
      // dynamic T4 (dynamicTriage.js): the casualties that need blood are held in T4, so the others do not lose resuscitation
      resuscitationInPlace: _hzBloodMod <= 1.0 || woundHazard.walkingBloodBankActive === true || woundHazard.bloodNeedHeld === true,
      antibioticsInStock: woundHazard.antibioticsInStock,
      dressingsInStock: woundHazard.dressingsInStock
    });
    const _hzCare = { ..._hzCareBase, tailScale: _mcTail?.late_death_scale ?? 1, tailTiming: _mcTail?.late_death_timing ?? 1 };
    // Contested and denied environments act through the curve (Dr. Tatar, 2026-10-10): the paradigm lengthens the
    // transit to Role 2 (fewer helicopters). Delay = expected transit under this paradigm minus under PERMISSIVE.
    const _permissiveParadigm = getParadigm ? getParadigm('PERMISSIVE') : EVACUATION_PARADIGMS?.PERMISSIVE;
    if (!_permissiveParadigm) {
      throw new Error('survivalModule.calculateComprehensiveDOW: evacuationParadigms has no PERMISSIVE paradigm (needed as the reference for the paradigm delay)');
    }
    const _permissiveTransit = calculateEvacuationTime(_permissiveParadigm, role2DistanceKm, environment);
    // Hold time before evacuation can start: the planner's own figure (Tab 2, operation_parameters.evacuation_hold_hours)
    // when set, otherwise the paradigm's default.
    const _plannerHold = scenario?.operation_parameters?.evacuation_hold_hours;
    let _holdHours;
    let _holdSource;
    if (_plannerHold !== undefined && _plannerHold !== null && _plannerHold !== '') {
      if (typeof _plannerHold !== 'number' || Number.isNaN(_plannerHold) || _plannerHold < 0 || _plannerHold > HOLD_HOURS_LIMIT) {
        throw new Error(`survivalModule.calculateComprehensiveDOW: operation_parameters.evacuation_hold_hours must be a number from 0 to ${HOLD_HOURS_LIMIT} (got: ${_plannerHold})`);
      }
      _holdHours = _plannerHold;
      _holdSource = 'PLANNER';
    } else {
      _holdHours = PARADIGM_HOLD_HOURS[paradigm];
      _holdSource = 'PARADIGM_DEFAULT';
      if (typeof _holdHours !== 'number') {
        throw new Error(`survivalModule.calculateComprehensiveDOW: PARADIGM_HOLD_HOURS has no entry for paradigm '${paradigm}'`);
      }
    }
    if (typeof woundHazard.walkingBloodBankActive !== 'boolean') {
      throw new Error(`survivalModule.calculateComprehensiveDOW: woundHazard.walkingBloodBankActive must be true or false (got: ${woundHazard.walkingBloodBankActive})`);
    }
    const _queueWaitHours = woundHazard.surgicalQueueWaitHours;
    if (typeof _queueWaitHours !== 'number' || Number.isNaN(_queueWaitHours) || _queueWaitHours < 0) {
      throw new Error(`survivalModule.calculateComprehensiveDOW: woundHazard.surgicalQueueWaitHours must be a number >= 0 (got: ${_queueWaitHours})`);
    }
    // R2-28: with the evacuation environment derived from the window, the typed label's extra transit is dropped (the window and the transit legs carry the same conditions)
    const _transitDelayMin = woundHazard.paradigmTransitDelayOff === true ? 0 : Math.max(0, paradigmTransit.expected_time_minutes - _permissiveTransit.expected_time_minutes);
    const _triageMixHz = typeof woundHazard.t1ToT4Share === 'number'
      ? { t1: t1Percent * (1 - woundHazard.t1ToT4Share), t2: t2Percent, t3: t3Percent, t4: t4Percent + t1Percent * woundHazard.t1ToT4Share }
      : { t1: t1Percent, t2: t2Percent, t3: t3Percent, t4: t4Percent };
    // One evaluation of the wound model for a given wait before evacuation (hours).
    const _hazardForHold = (holdHours, t2ExtraWaitHours = 0) => {
      const paradigmDelayMin = _transitDelayMin + holdHours * 60;
      const arrivalHours = woundHazard.arrivalHours + paradigmDelayMin / 60;
      // Surgery cannot start at the moment of arrival: a minimum pre-operative interval applies.
      // plus the wait in the surgical queue behind other casualties (resourceModule surgical queue cascade, previous day).
      const noQueueSurgery = Math.max(evacuationTimeMinutes / 60 + paradigmDelayMin / 60, arrivalHours + PREOP_INTERVAL_MIN.minutes / 60);
      const surgeryHours = noQueueSurgery + _queueWaitHours;
      const hz = baseDowFromWounds({
        woundDistribution,
        triageMix: _triageMixHz,
        t3Mortality,
        t4Mortality,
        arrivalHours,
        surgeryHours,
        // priority queue (dynamic T4 surgeon trigger): surgeons take T1 first, so the T1 who are operated do not wait in the queue; the T1 who
        // cannot be operated are held in T4 instead. T2 and below keep the average wait.
        surgeryHoursT1: woundHazard.t1PriorityQueue === true ? noQueueSurgery : surgeryHours,
        t2ExtraWaitHours,   // R2-29: T2 and below wait longer than T1 in the evacuation queue
        care: _hzCare
      });
      return { hz, paradigmDelayMin, arrivalHours, surgeryHours };
    };
    // Derived wait (evacuationWindowModel.js, R2-23): the wait before the first movement is a spread, not one number. The wound model is
    // evaluated at the middle of equal-probability slices of the spread and averaged. A planner's own hold hours still win (legacy input).
    const _spreadPoints = (Array.isArray(woundHazard.waitSpreadHours) && woundHazard.waitSpreadHours.length > 0 && _holdSource !== 'PLANNER')
      ? woundHazard.waitSpreadHours : null;
    let _hz;
    let _paradigmDelayMin;
    let _hzArrivalHours;
    let _hzSurgeryHours;
    if (_spreadPoints) {
      _spreadPoints.forEach((h) => {
        if (typeof h !== 'number' || Number.isNaN(h) || h < 0) throw new Error(`survivalModule.calculateComprehensiveDOW: woundHazard.waitSpreadHours must be numbers >= 0 (got: ${h})`);
      });
      const _otherSpread = Array.isArray(woundHazard.waitSpreadHoursOther) && woundHazard.waitSpreadHoursOther.length === _spreadPoints.length ? woundHazard.waitSpreadHoursOther : null;
      const runs = _spreadPoints.map((h, i) => _hazardForHold(h, _otherSpread ? Math.max(0, _otherSpread[i] - h) : 0));
      const avg = (f) => runs.reduce((a, r) => a + f(r), 0) / runs.length;
      const byRegion = {};
      Object.keys(runs[0].hz.byRegion).forEach((reg) => {
        byRegion[reg] = {};
        Object.keys(runs[0].hz.byRegion[reg]).forEach((k) => { byRegion[reg][k] = avg((r) => r.hz.byRegion[reg][k]); });
      });
      _hz = { dowRate: avg((r) => r.hz.dowRate), prehospitalRate: avg((r) => r.hz.prehospitalRate), dowRateOfArrivals: avg((r) => r.hz.dowRateOfArrivals), byRegion };
      _paradigmDelayMin = avg((r) => r.paradigmDelayMin);
      _hzArrivalHours = avg((r) => r.arrivalHours);
      _hzSurgeryHours = avg((r) => r.surgeryHours);
      _holdHours = _spreadPoints.reduce((a, b) => a + b, 0) / _spreadPoints.length;
      _holdSource = 'DERIVED_SPREAD';
    } else {
      const one = _hazardForHold(_holdHours);
      _hz = one.hz;
      _paradigmDelayMin = one.paradigmDelayMin;
      _hzArrivalHours = one.arrivalHours;
      _hzSurgeryHours = one.surgeryHours;
    }
    woundHazardReport = {
      mode: woundHazard.mode,
      applied: woundHazard.mode === 'ON',
      // ON: blood shortage, the paradigm multiplier and the medical-gap term act through the curve (skipped below);
      // TCCC 2025 is report-only and FRT is scaled by blood in the calculator. See docs/planning/HAZARD_OVERLAP_CHECK.md.
      double_count_guard_complete: woundHazard.mode === 'ON',
      paradigm_delay_min: _paradigmDelayMin,
      paradigm_transit_delay_min: _transitDelayMin,
      paradigm_hold_hours: _holdHours,
      paradigm_hold_source: _holdSource,
      surgical_queue_wait_hours: _queueWaitHours,
      walking_blood_bank_active: woundHazard.walkingBloodBankActive === true,
      t1_held_in_t4_share: typeof woundHazard.t1ToT4Share === 'number' ? woundHazard.t1ToT4Share : 0,
      base_dow_flat: Math.round(baseDOWFlat * 1000) / 10,
      base_dow_hazard: Math.round(_hz.dowRate * 1000) / 10,
      prehospital_rate: Math.round(_hz.prehospitalRate * 1000) / 10,
      // Deaths before surgery = before arrival + DOW. DOW alone FALLS when evacuation is slow (the dying die earlier,
      // before arrival; Mabry 2015), so this total is the figure to read.
      // DOW as a share of those who arrived alive (published definition; compare with US 2.5% of all battle injuries / 4.56% of non-RTD).
      dow_rate_of_arrivals: Math.round(_hz.dowRateOfArrivals * 1000) / 10,
      deaths_before_surgery_rate: Math.round((_hz.prehospitalRate + _hz.dowRate) * 1000) / 10,
      arrival_hours: _hzArrivalHours,
      surgery_hours: _hzSurgeryHours,
      preop_interval_min: PREOP_INTERVAL_MIN.minutes,
      care: _hzCare,
      wait_spread_hours: _spreadPoints,
      by_region: _hz.byRegion
    };
    if (woundHazard.mode === 'ON') {
      baseDOWRate = _hz.dowRate;
    }
  }

  // 10. Calculate medical gap analysis using imported functions
  let medicalGapAnalysis = null;
  let gapDOWImpact = null;
  
  if (calculateMedicalGap) {
    medicalGapAnalysis = calculateMedicalGap({
      evacuation_time_minutes: evacuationTimeMinutes,
      mtf_status: mtfStatus,
      paradigm: paradigm,
      golden_hour_achieved: goldenHourAchieved
    });
  }
  
  if (calculateGapDOW && medicalGapAnalysis) {
    gapDOWImpact = calculateGapDOW(medicalGapAnalysis);
  }

  // 11. Apply MTF capability modifier
  // BUG-057 FIX: Guard against NaN from getDOWMultiplierFromCapability
  let mtfCapabilityMultiplier = getDOWMultiplierFromCapability(mtfStatus.medical_capability);
  if (mtfCapabilityMultiplier === undefined || mtfCapabilityMultiplier === null || isNaN(mtfCapabilityMultiplier)) {
    mtfCapabilityMultiplier = 1.0;  // Default: no modification
    console.warn(`[BUG-057 FIX] mtfCapabilityMultiplier was invalid, using default 1.0`);
  }
  let adjustedDOWRate = baseDOWRate * mtfCapabilityMultiplier;

  // 11.5 ENH-032: Apply night ops treatment quality degradation.
  // NIGHT_OPS_LEVELS.treatment_quality < 1.0 → degraded care at night → higher DOW rate.
  // Sourced from scenario.operation_parameters.night_ops_config.modifiers (pre-computed by Tab2).
  // Formula: DOW multiplier = 1 / treatment_modifier (quality 0.85 → ×1.176, 0.90 → ×1.111, 0.95 → ×1.053).
  const _nightOpsScenCfg = scenario?.operation_parameters?.night_ops_config;
  const _nightTreatmentMod = (_nightOpsScenCfg?.enabled && (_nightOpsScenCfg?.modifiers?.treatment_modifier > 0))
    ? _nightOpsScenCfg.modifiers.treatment_modifier
    : 1.0;
  if (_nightTreatmentMod < 1.0) {
    adjustedDOWRate *= (1.0 / _nightTreatmentMod);
  }

  // 11.6 ENH-039: Apply R2B bed occupancy degradation to DOW rate.
  // 2026-10-10 (Dr. Tatar): a RAMP, not a step. No effect up to BED_OCCUPANCY_MODEL.dow_ramp.start (70%), then the effect
  // rises linearly to the full multiplier at 100% (x1.5 = getDOWMultiplierFromCapability(0.70)). The old step (x1.1 at
  // 70%, x1.3 at 85%, x1.5 at 95%) gave a jump of x1.5 in one day when the beds filled. bedOccupancyPct is a percentage.
  if (bedOccupancyPct > 0) {
    const _ramp = BED_OCCUPANCY_MODEL.dow_ramp;
    const _fullEffect = getDOWMultiplierFromCapability(_ramp.full_effect_capability);
    const _share = Math.min(1, Math.max(0, (bedOccupancyPct / 100 - _ramp.start) / (_ramp.full - _ramp.start)));
    const _bedDOWMod = 1 + (_fullEffect - 1) * _share;
    if (_bedDOWMod > 1.0) {
      adjustedDOWRate *= _bedDOWMod;
    }
  }

  // 12. Apply blood shortage modifier
  // BUG-057 FIX: Guard against NaN from blood shortage calculation
  const bloodAlertLevel = medicalConfig?.blood_config?.alert?.current_level || 'GREEN';
  const bloodAnalysis = getBloodShortageDOWModifier ? 
    getBloodShortageDOWModifier(bloodAlertLevel) :
    { modifier: 1.0 };
  let bloodModifier = (typeof bloodAnalysis === 'object' ? bloodAnalysis.modifier : bloodAnalysis);
  if (bloodModifier === undefined || bloodModifier === null || isNaN(bloodModifier)) {
    bloodModifier = 1.0;
    console.warn(`[BUG-057 FIX] bloodModifier was invalid, using default 1.0`);
  }
  const _hazardOn = woundHazard?.mode === 'ON';
  adjustedDOWRate *= _hazardOn ? 1.0 : bloodModifier;   // ON: blood acts through the curve (resuscitation flag)

  // 13. Apply triage error modifier
  // BUG-057 FIX: Guard against NaN from triage performance
  let triageModifier = triagePerformance?.impact?.dow_modifier;
  if (triageModifier === undefined || triageModifier === null || isNaN(triageModifier)) {
    triageModifier = 1.0;
    // BUG-057-NOISE: warning suppressed — fires for every unit during Quick Build (500+ times).
    // Root cause: triagePerformance.impact.dow_modifier not populated for all scenarios.
    // Fallback to 1.0 is correct behaviour; no user action required.
  }
  adjustedDOWRate *= triageModifier;

  // 14. Apply paradigm modifier
  // CALC-003 (2026-10-09): documented DOW multipliers (CASUALTY_DISTRIBUTION.dow_modifiers:
  // contested x1.4, denied x2.0, permissive x1.0). Replaces the ad-hoc (2 - t1_survival_rate)
  // factor (x1.08 / x1.24 / x1.46), which was not normalised to permissive and was derived from the
  // same evacuation times that now drive the T1 curve directly.
  const paradigmDOWModifier = getParadigmDOWModifier(paradigm);
  adjustedDOWRate *= _hazardOn ? 1.0 : paradigmDOWModifier;   // ON: the paradigm acts through the curve (transit delay)

  // 15. Apply medical gap DOW impact if calculated
  if (!_hazardOn && gapDOWImpact?.additional_dow_rate && !isNaN(gapDOWImpact.additional_dow_rate)) {
    adjustedDOWRate += gapDOWImpact.additional_dow_rate;
  }

  // 16. Optional: Run Monte Carlo simulation for confidence interval
  let monteCarloResult = null;
  if (runSurvivalMonteCarlo && config.runMonteCarlo) {
    // Generate evacuation time distribution
    const evacTimeDistribution = getRandomEvacuationTime ? 
      Array.from({ length: 100 }, () => getRandomEvacuationTime(paradigm, role2DistanceKm, environment)) :
      Array.from({ length: 100 }, () => evacuationTimeMinutes * (0.8 + Math.random() * 0.4));
    
    monteCarloResult = runSurvivalMonteCarlo({
      evacuation_times: evacTimeDistribution,
      tccc_quality: tcccAnalysis.combined.tccc_quality_assessment,
      paradigm: paradigm
    });
  }

  // 17. Clamp to reasonable range
  // BUG-057 FIX: Final NaN guard before return
  // SVM-09 DEFERRED: 0.12 fallback same as above — needs named constant.
  // SVM-09 FIX: 0.12 replaced with NATO_BASE_DOW_RATE from CCC (February 24, 2026).
  if (isNaN(adjustedDOWRate) || adjustedDOWRate === undefined || adjustedDOWRate === null) {
    console.warn(`[BUG-057 FIX] CRITICAL: adjustedDOWRate was still NaN, using NATO fallback ${(NATO_BASE_DOW_RATE * 100).toFixed(0)}%`);
    adjustedDOWRate = NATO_BASE_DOW_RATE;  // SVM-09
  }
  adjustedDOWRate = Math.min(0.45, Math.max(0.02, adjustedDOWRate));

  return {
    dow_rate: adjustedDOWRate,
    dow_percent: Math.round(adjustedDOWRate * 100 * 10) / 10,

    analysis: {
      // Platinum period
      platinum_period: {
        time_to_first_intervention: firstInterventionAnalysis.time_to_first_intervention_minutes,
        achieved: firstInterventionAnalysis.platinum_achieved,
        survival_bonus: firstInterventionAnalysis.platinum_survival_bonus
      },

      // TCCC effects
      tccc: {
        tourniquet: tcccAnalysis.tourniquet,
        txa: tcccAnalysis.txa,
        hypothermia: tcccAnalysis.hypothermia,
        prolonged_field_care: tcccAnalysis.prolonged_field_care,
        combined_dow_reduction: tcccAnalysis.combined.dow_reduction_percent,
        quality_assessment: tcccAnalysis.combined.tccc_quality_assessment
      },

      // Evacuation
      evacuation: {
        method: evacuationMethod,
        time_minutes: Math.round(evacuationTimeMinutes),
        role2_distance_km: role2DistanceKm,
        golden_hour_achieved: goldenHourAchieved,
        golden_hour_benefit: goldenHourBenefitData,
        transport: {
          paradigm_expected_minutes: paradigmTransit.expected_time_minutes,
          helicopter_availability_percent: paradigmTransit.helicopter.availability,
          ground_minutes: paradigmTransit.ground.time_minutes,
          golden_hour_achievable: goldenHourTransport.achievable,
          golden_hour_margin_minutes: goldenHourTransport.margin_minutes,
          terrain_evacuation_speed: terrainEvacuationSpeed
        },
        paradigm: paradigm,
        paradigm_config: paradigmConfig
      },

      // Triage - include data from imported functions
      triage: {
        officer_condition: triagePerformance.condition,
        accuracy_percent: triagePerformance.performance.accuracy_percent,
        undertriage_impact: triagePerformance.impact.additional_deaths_from_undertriage,
        distribution_from_constants: triageDistributionData,
        accuracy_from_constants: triageAccuracyData,
        mascal_adjusted: isMascal ? adjustedTriageDistribution : null
      },

      // Medical system - include MTF constants
      medical_system: {
        mtf_status: mtfStatus.mtf_status,
        capability: mtfStatus.medical_capability,
        blood_status: bloodAlertLevel,
        // MTF constants for educational context
        mtf_deployment_times: MTF_DEPLOYMENT_TIMES || null,
        posture_mtf_status: POSTURE_MTF_STATUS || null,
        capability_dow_multipliers: MEDICAL_CAPABILITY_DOW_MULTIPLIER || null,
        prolonged_field_care_protocols: PROLONGED_FIELD_CARE || null
      },

      // Medical gap analysis
      medical_gap: {
        analysis: medicalGapAnalysis,
        dow_impact: gapDOWImpact
      },

      // Blood status - include BLOOD_ALERT_LEVELS for educational context
      blood: {
        current_level: bloodAlertLevel,
        dow_modifier: typeof bloodAnalysis === 'object' ? bloodAnalysis.modifier : bloodAnalysis,
        alert_levels_reference: BLOOD_ALERT_LEVELS || null
      },

      // Survival by category - include t3Baseline for educational tracking
      survival_by_category: {
        t1_immediate: Math.round(t1WithTCCC * 100),
        t2_delayed: Math.round(t2Baseline * 100),
        t3_minimal: Math.round((1 - t3Mortality) * 100),
        t3_baseline_survival: Math.round(t3Baseline * 100), // Educational: time-based T3 survival
        t4_expectant: Math.round((1 - t4Mortality) * 100)
      },

      // Survival curves educational data
      survival_curves: {
        t1_curve: T1_SURVIVAL_CURVE || null,
        t2_curve: T2_SURVIVAL_CURVE || null,
        t3_curve: T3_SURVIVAL_CURVE || null,
        intervention_timing: INTERVENTION_TIMING_EFFECTS || null
      },

      // DOW breakdown
      dow_contribution_by_category: {
        t1_immediate: Math.round(t1DOWContribution * 1000) / 10,
        t2_delayed: Math.round(t2DOWContribution * 1000) / 10,
        t3_minimal: Math.round(t3DOWContribution * 1000) / 10,
        t4_expectant: Math.round(t4DOWContribution * 1000) / 10
      },

      // Modifiers applied
      modifiers_applied: {
        base_dow_rate: Math.round(baseDOWRate * 1000) / 10,
        wound_type_applied: woundTypeApplied,      // WTD-01: true when wound distribution refined DOW
        wound_hazard: woundHazardReport,            // null when the hazard model is OFF
        operation_type: operationType || null,      // WTD-01: operation type used for refinement
        mtf_capability_modifier: mtfCapabilityMultiplier,
        blood_shortage_modifier: typeof bloodAnalysis === 'object' ? bloodAnalysis.modifier : bloodAnalysis,
        triage_error_modifier: Math.round(triagePerformance.impact.dow_modifier * 100) / 100,
        paradigm_modifier: paradigmDOWModifier,
        paradigm_modifier_applied: _hazardOn ? 1.0 : paradigmDOWModifier,
        blood_modifier_applied: _hazardOn ? 1.0 : (typeof bloodAnalysis === 'object' ? bloodAnalysis.modifier : bloodAnalysis),
        medical_gap_impact: gapDOWImpact?.additional_dow_rate || 0,
        final_dow_rate: Math.round(adjustedDOWRate * 1000) / 10
      },
      
      // Optional Monte Carlo simulation results
      monte_carlo: monteCarloResult
    },

    recommendations: [
      ...generateDOWRecommendations(tcccAnalysis, goldenHourAchieved, evacuationMethod),
      ...triagePerformance.recommendations
    ],

    educational_insight: generateDOWEducationalInsight({
      tccc: tcccAnalysis,
      goldenHour: goldenHourAchieved,
      evacTime: evacuationTimeMinutes,
      method: evacuationMethod,
      paradigm
    })
  };
}

/**
 * Generate DOW recommendations
 */
function generateDOWRecommendations(tccc, goldenHour, method) {
  const recs = [];

  if (tccc.combined.tccc_quality_assessment === 'POOR' || 
      tccc.combined.tccc_quality_assessment === 'CRITICAL') {
    recs.push('URGENT: Improve CLS training - TCCC quality is critical to survival');
  }

  if (!goldenHour && method === 'GROUND') {
    recs.push('Consider forward positioning of Role 2 to achieve golden hour');
    recs.push('Request helicopter MEDEVAC allocation if AD threat permits');
  }

  if (tccc.hypothermia.risk_level === 'HIGH' || tccc.hypothermia.risk_level === 'CRITICAL') {
    recs.push('Deploy hypothermia prevention kits - cold increasing mortality');
  }

  if (!tccc.txa.administered) {
    recs.push('Ensure TXA in all CLS kits - proven 32% mortality reduction');
  }

  return recs;
}

/**
 * Generate educational insight text
 */
function generateDOWEducationalInsight(data) {
  let insight = '';

  // TCCC insight
  if (data.tccc.tourniquet.applied && data.tccc.tourniquet.time_minutes <= 5) {
    insight += `Excellent tourniquet timing (${data.tccc.tourniquet.time_minutes}min) - `;
    insight += `achieving ${data.tccc.tourniquet.survival_rate}% survival. `;
  } else if (data.tccc.tourniquet.applied) {
    insight += `Tourniquet at ${data.tccc.tourniquet.time_minutes}min - `;
    insight += `faster application would improve from ${data.tccc.tourniquet.survival_rate}% toward 87%. `;
  }

  // Golden hour insight
  if (data.goldenHour) {
    insight += `Golden Hour achieved (${Math.round(data.evacTime)}min). `;
  } else {
    insight += `Golden Hour NOT achieved (${Math.round(data.evacTime)}min > 60min). `;
    insight += `Each minute beyond 60 increases T1 mortality. `;
  }

  // Paradigm insight
  if (data.paradigm === 'DENIED') {
    insight += 'DENIED airspace severely limiting MEDEVAC - expect increased DOW. ';
  } else if (data.paradigm === 'CONTESTED') {
    insight += 'Contested environment reducing helicopter availability. ';
  }

  // Method insight
  if (data.method === 'GROUND') {
    insight += 'Ground evacuation adds ~50% to evacuation time vs helicopter. ';
  }

  return insight;
}

// ============================================================================
// EXPORTS
// ============================================================================

// SVM-04: NIGHT_FIRST_AID_MODIFIER is now imported from prehospitalInterventions.js.
// Re-exported here for backward compatibility with any code importing it from this module.
export {
  CLS_TRAINING_QUALITY,       // SVM-02 DEFERRED: still local (key mismatch with canonical)
  TERRAIN_FIRST_AID_MODIFIERS, // SVM-03 DEFERRED: still local (terrainFactors.js missing)
  NIGHT_FIRST_AID_MODIFIER    // SVM-04: re-export of import from prehospitalInterventions.js
};

// Named default export (fixes lint warning)
const survivalModule = {
  // Main calculation functions
  calculateTimeToFirstIntervention,
  calculateTCCCInterventionEffects,
  calculateTriageOfficerPerformance,
  calculateComprehensiveDOW,

  // Local constants (exported for testing/reference)
  CLS_TRAINING_QUALITY,
  TERRAIN_FIRST_AID_MODIFIERS,
  NIGHT_FIRST_AID_MODIFIER,

  // BUG-057-NOISE: one-time warn flag — reset before each full calculation run
  _triageWarnFired: false,
  // INC-002: paradigm missing flag — set when evacuation paradigm lookup fails; reset per run
  _paradigmMissing: null,
  // CALC-005: set when the scenario has no evacuation_paradigm and the documented default was used
  _paradigmDefaulted: false,
  resetWarnings() { this._triageWarnFired = false; this._paradigmMissing = null; this._paradigmDefaulted = false; }
};

export default survivalModule;
